import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { useAboutContext } from "../../context/About_context";
import Loader from "../../Common/loader/Loader";
import No_img from "../../Assets/No_img.jpg";
import { renderTitleWithGradient } from "../../utils/helper";
import "./Conferences.css";
import { FaChevronRight } from "react-icons/fa6";

/* ---------- Scroll Reveal Component (Framer Motion) ---------- */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{
        duration: 0.7,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Conferences = () => {
  const { conference_data, conference_loading, GetConference } =
    useAboutContext();

  useEffect(() => {
    GetConference();
  }, []); // eslint-disable-line

  if (
    conference_loading &&
    (!conference_data || Object.keys(conference_data).length === 0)
  ) {
    return <Loader fullScreen={true} text="Loading Conferences..." />;
  }

  return (
    <div className="conf-page">
      <Helmet>
        <title>Conferences | Bharat Buildcon</title>
      </Helmet>
      {/* ---------------- ULTRA MODERN HERO & SHOWCASE SECTION ---------------- */}
      <section className="conf-hero-section">
        {/* Light Design Background */}
        <div className="conf-light-design-bg" aria-hidden="true">
          <div className="conf-light-flare conf-flare-left" />
          <div className="conf-light-flare conf-flare-right" />

          <div className="conf-geo-accent geo-top-left" />
          <div className="conf-geo-accent geo-top-right" />
          <div className="conf-geo-accent geo-mid-left" />
          <div className="conf-geo-accent geo-mid-right" />
        </div>

        <div className="conf-hero-container">
          <Reveal>
            <div className="clean-breadcrumb-wrapper">
              <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
                <Link to="/" className="bc-home-link">
                  HOME
                </Link>
                <span className="bc-slash">/</span>
                <span className="bc-active-page">CONFERENCES</span>
              </nav>
            </div>

            {conference_data?.conference_title && (
              <h1 className="conf-hero-title">
                {renderTitleWithGradient(conference_data.conference_title)}
              </h1>
            )}

            {conference_data?.conference_description && (
              <div
                className="conf-hero-subtitle"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(conference_data.conference_description),
                }}
              />
            )}
          </Reveal>

          {/* Showcase Banner */}
          {conference_data?.conference_image && (
            <Reveal delay={150}>
              <div className="conf-showcase-frame">
                <div className="conf-showcase-inner">
                  <img
                    src={conference_data.conference_image}
                    alt={
                      conference_data?.conference_title || "Conference Banner"
                    }
                    className="conf-showcase-img"
                  />
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ---------------- CONFERENCES LEAD BY SECTION ---------------- */}
      <section className="conf-leadby-section">
        {/* Soft Organic Background Wave */}
        <div className="conf-organic-wave-bg" aria-hidden="true">
          <svg
            className="conf-liquid-wave-svg"
            viewBox="0 0 1440 850"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M-50 -50 L 1490 -50 L 1490 120 C 950 280, 450 60, -50 220 Z"
              fill="url(#saffron-organic-grad)"
            />
            <path
              d="M-50 870 L 1490 870 L 1490 520 C 1000 380, 480 620, -50 480 Z"
              fill="url(#green-organic-grad)"
            />
            <path
              d="M-50 260 C 500 100, 980 320, 1490 160"
              stroke="rgba(242, 101, 34, 0.12)"
              strokeWidth="2.5"
            />
            <path
              d="M-50 520 C 480 640, 960 420, 1490 560"
              stroke="rgba(74, 139, 44, 0.12)"
              strokeWidth="2.5"
            />
            <defs>
              <linearGradient
                id="saffron-organic-grad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#fff3ec" stopOpacity="0.95" />
                <stop offset="60%" stopColor="#ffe6d9" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="green-organic-grad"
                x1="100%"
                y1="100%"
                x2="0%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#f0fdf4" stopOpacity="0.95" />
                <stop offset="60%" stopColor="#dcfce7" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="conf-leadby-container">
          <Reveal>
            <div className="conf-section-center-header">
              {conference_data?.conference_logo && (
                <div className="conf-leadby-brand-header-wrap">
                  <div className="conf-leadby-brand-header">
                    <img
                      src={conference_data.conference_logo}
                      alt="Conference Logo"
                      className="conf-leadby-brand-logo"
                    />
                  </div>
                </div>
              )}

              {conference_data?.conference_text && (
                <>
                  <h2 className="conf-section-title">
                    {conference_data.conference_text}
                  </h2>
                  <div className="conf-title-underline" />
                </>
              )}
            </div>
          </Reveal>

          {/* Dynamic Cards Grid Wrapper */}
          {conference_data?.conference_leads &&
            conference_data.conference_leads.length > 0 && (
              <div className="conf-cards-wrapper">
                {conference_data.conference_leads.map((lead, idx) => (
                  <Reveal
                    key={lead.id || idx}
                    delay={idx * 100}
                    className="conf-card-reveal-wrap"
                  >
                    <div
                      className="conf-leader-card"
                      tabIndex={0}
                      aria-label={`View details for ${lead.name}`}
                    >
                      <div className="conf-card-inner">
                        {/* Prominent Large Logo Box */}
                        <div className="conf-card-logo-box">
                          <div className="conf-logo-render-area">
                            <div className="conf-big-logo-container">
                              <img
                                src={lead.image_full_path || lead.image || No_img}
                                alt={lead.name}
                                className="conf-logo-img"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Solid Blue Bottom Tag Box */}
                        <div className="conf-card-bottom-tag">
                          <span className="conf-tag-code">{lead.name}</span>
                          <FaChevronRight className="conf-tag-arrow" />
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
        </div>
      </section>
    </div>
  );
};

export default Conferences;
